import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import getConfig from 'next/config';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/core/device';

import { useBrand } from '@atc/brand';
import { eventBus } from '@atc/cai-event-bus';
import { useFeatures } from '@atc/features';
import { getFirstPartyVistorId, getPixallId } from '@atc/pixall-fns';
import { ClientOnly } from '@atc/react-client-only';
import { formatCurrency } from '@atc/string-fns';
import { calculateDownPayment } from '@atc/wallet-payment-fns';

import {
    LoadingSpinner,
    ModalScrollPanel,
    Panel,
    StickyContainer,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { ownerEmailClick, ownerEmailSent, telMetricsPhoneTextClick } from 'reaxl-analytics-handlers';
import { sendEmailAndSaveDeal } from 'reaxl-email';
import { OwnerChatEvents } from 'reaxl-molecules';
import { AvailableOffers, DigitalRetailOverlayCTA, getAppliedDealerSettings, getIncentives, transformIncentives } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import { getDeal } from 'wallet-deals';
import {
    UDS_CONFIG_COMPLETED,
    UDS_CONFIG_REQUEST,
    UDS_CONFIG_UPDATE_COMPLETED,
    useUDSUpdate,
    useWalletCalcConfig,
    WALLET_EMAIL_DEALER_REQUEST,
} from 'wallet-manager';

import { getCompareListingsIds, getCompareListingsToggle } from '@/utilities/compareListingsHandler';
import configureExperianTile from '@/utilities/configureExperianTile';
import getVhrOverviewList from '@/utilities/getVhrOverviewList';
import { getAbsolutePathWithListingId } from '@/utilities/vdpUrlBuilder';

import remoteActions from '@/actions/vdp/remoteActions';

import { emailFormEngagementClick } from '@/analytics/srp/owner/ownerEmailEvents';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import authDuck from '@/ducks/authDuck.js';
import birfDuck from '@/ducks/birfDuck.js';
import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import userDuck from '@/ducks/userDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpActiveEmailListingDuck from '@/ducks/vdp/vdpActiveEmailListingDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpAvailableOffersDuck from '@/ducks/vdp/vdpAvailableOffersDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import EmailOwnerFetcher from '@/fetchers/EmailOwnerFetcher';
import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';
import SimilarListingsFetcher from '@/fetchers/SimilarListingsFetcher';
import SmsSubscribeFetcher from '@/fetchers/SmsSubscribeFetcher';
import PersistDealDataService from '@/fetchers/vdp/PersistDealDataService';
import PsxPrequalEligibilityCheckFetcher from '@/fetchers/vdp/PsxPrequalEligibilityCheckFetcher';

import useLeadAdditionalInventory from '@/hooks/useLeadAdditionalInventory';
import usePreQualEligibility from '@/hooks/usePreQualEligibility';

import EmailFormNextStepsContainer from '@/containers/EmailFormNextStepsContainer';

import AdditionalInventoryModal from '@/components/AdditionalInventoryModal';
import Anchor from '@/components/Anchor/index.js';
import { DynamicPlaceholder } from '@/components/DynamicPlaceholder/DynamicPlaceholder';
import LazyComponent from '@/components/LazyComponent';
import PriceSection from '@/components/PriceSection';
import TreehouseAdPlacement from '@/components/TreehouseAdPlacement';
import AffixedSidebar from '@/components/vehicledetails/AffixedSidebar';
import OfferSummary from '@/components/vehicledetails/OfferSummary';

import LeaderBoardAdSlotContainer from './LeaderBoardAdSlotContainer';
import LenderPreQualWrapper from './LenderPreQualWrapper';
import ListingErrorContainer from './ListingErrorContainer';
import MediaGallery from './MediaGallery';
import PricingSnapshotContainer from './PricingSnapshotContainer';
import PrimaryVdpCallsToActionContainer from './PrimaryVdpCallsToActionContainer';
import PurchaseWithConfidenceSnapshotContainer from './PurchaseWithConfidenceSnapshotContainer';
import ShowcaseAdSlotContainer from './ShowcaseAdSlotContainer';
import SpecificationsGridContainer from './SpecificationsGridContainer';
import StandaloneCreditAppContainer from './StandaloneCreditAppContainer';
import VdpPaginationContainer from './VdpPaginationContainer';
import VdpShoppingAssistantContainer from './VdpShoppingAssistantContainer';
import VehicleDetailsEmailModalContainer from './VehicleDetailsEmailModalContainer';
import VehicleDetailsTitleContainer from './VehicleDetailsTitleContainer';

const LowerDetails = dynamic(() => import('@/containers/vdp/sections/LowerDetails'), { ssr: false });

const additionalVehicleDetailsHeight = { xs: 400, sm: 400, md: 500, lg: 500 };
const AdditionalVehicleDetailsContainer = dynamic(() => import('@/containers/vdp/AdditionalVehicleDetailsContainer'), {
    ssr: false,
    loading: () => ((<DynamicPlaceholder height={additionalVehicleDetailsHeight} />)),
});

const batteryHealthHeight = { xs: 410, sm: 286, md: 375, lg: 290 };
const BatteryHealthContainer = dynamic(
    () => import(
        /* webpackChunkName: "BatteryHealthContainer" */
        '@/containers/vdp/BatteryHealthContainer'
    ),
    {
        loading: () => (<DynamicPlaceholder height={batteryHealthHeight} />),
        ssr: false,
    }
);
const certifiedProgramHeights = { xs: 230, sm: 230, md: 380, lg: 380 };
const CertifiedProgramOfferContainer = dynamic(() => import('@/containers/vdp/CertifiedProgramOfferContainer'), {
    loading: () => (<DynamicPlaceholder height={certifiedProgramHeights} />),
    ssr: false,
});
const MyWalletContainer = dynamic(() => import('@/containers/vdp/MyWalletContainer'), {
    ssr: false,
    loading: () => (
        <LoadingSpinner
            id="nativeDealContainer"
            height={400}
        />
    ),
});
const PreQualCTAContainer = dynamic(() => import('@/containers/vdp/PreQualCTAContainer'), { ssr: false });
const pricingHeight = { xs: 872, sm: 872, md: 872, lg: 884 };
const PricingDetailsContainer = dynamic(() => import('@/containers/vdp/PricingDetailsContainer'), {
    loading: () => (<DynamicPlaceholder height={pricingHeight} />),
    ssr: false,
});

const purchaseConfidenceHeight = { xs: 225, sm: 225, md: 244, lg: 244 };
const PurchaseWithConfidenceContainer = dynamic(() => import('@/containers/vdp/PurchaseWithConfidenceContainer'), {
    loading: () => (<DynamicPlaceholder height={purchaseConfidenceHeight} />),
    ssr: false,
});
const ratingsAndReviewsHeight = { xs: 820, sm: 550, md: 550, lg: 430 };
const RatingsAndReviewsContainer = dynamic(() => import('@/containers/vdp//RatingsAndReviews/RatingsAndReviewsContainer'), {
    loading: () => (<DynamicPlaceholder height={ratingsAndReviewsHeight} />),
    ssr: false,
});
const StickyHeaderContainer = dynamic(() => import('@/containers/vdp/StickyHeaderContainer'), { ssr: false });
const VehicleFeaturesContainer = dynamic(() => import('@/containers/vdp/VehicleFeaturesContainer/VehicleFeaturesContainer'), { ssr: false });
const VhrOverviewContainer = dynamic(() => import('@/containers/vdp/VhrOverviewContainer'), { ssr: false });

// Default
const DealerLeadsContainer = dynamic(() => import('./DealerLeadsContainer'), { ssr: false });

// PSX
const PsxBubbleChat = dynamic(() => import('@/containers/vdp/psx/PsxBubbleChat'), { ssr: false });
const PsxButton = dynamic(() => import('@/containers/vdp/psx/PsxButton'), { ssr: false });
const PsxLeadsContainer = dynamic(() => import('@/containers/vdp/psx/PsxLeadsContainer'), { ssr: false });
const PsxPrimerContainer = dynamic(() => import('@/containers/vdp/psx/PsxPrimerContainer'), { ssr: false });
const PsxVerificationContainer = dynamic(() => import('@/containers/vdp/psx/PsxVerificationContainer'), { ssr: false });

const similarIventoryFallback = (
    <div
        id="placeholder-similar-inv"
        style={{ height: 370 }}
    />
);
const SimilarInventoryContainer = dynamic(() => import('./SimilarInventoryContainer'), {
    ssr: true,
    loading: () => similarIventoryFallback,
});

const KeywordSearchModalContainer = dynamic(() => import(
    /* webpackChunkName: "KeywordSearchModalContainer" */
    '@/containers/KeywordSearchModalContainer'
));

const ModelInformationModal = dynamic(() => import(
    /* webpackChunkName: "ModelInformationModal" */
    '@/components/ModelInformation'
    , { ssr: false }
));

/*
 * !xs:
 * `-----------------------------`
 * |    Sticky Header (<lg)      |
 * |.............................|
 * |         Navigation          |
 * |.............................|
 * | Banner                      |
 * |.............................|
 * |                  |          |
 * | Media            | Sidebar  |
 * | Upper Details    |          |
 * |.............................|
 * | Lower Details               |
 * |-----------------------------|
 * | Support Containers (hidden) |
 *  ----------------------------
 *
 * xs:
 * `------------------`
 * |  Sticky Header   |
 * |..................|
 * |  Navigation      |
 * |..................|
 * |  Banner          |
 * |..................|
 * |  Media           |
 * |..................|
 * |  Sidebar         |
 * |..................|
 * |  Upper Details   |
 * |..................|
 * |        AD        |
 * |..................|
 * |  Lower Details   |
 * |------------------|
 * | Support (hidden) |
 *  ------------------
 *
 * Default Banner Contents
 * 1. Listing Error
 * 2. Title Container
 *
 * Default Media Contents
 * 1. Media Gallery
 * 2. Specifications
 *
 * Default Sidebar Contents
 * 1. Pricing Snapshot
 * 2. Prequal
 * 3. Esntial
 * 4. Owner Details Snapshot
 * 5. Email Form
 * 6. Ad
 *
 * Default Upper Details Contents
 * 1. Wallet / NDS
 * 2. Seller Comments
 * 3. Vehicle Features
 * 4. Price Details
 * 5. Certified Pre-Owned
 * 6. Purchase With Confidence
 * 7. Ads
 * 8. Rating and Reviews
 *
 * Default Lower Details Contents
 * 1. Owner Details
 * 2. Similar Vehicles
 * 3. At Home Services
 * 4. Additional Resources
 * 5. Fraud Report
 *
 * Default Support Containers
 * 1. Email Modal
 * 2. Third Party Chat Events
 * 3. Stand Alone Credit App
 *
*/

// Should probably clean this up so we don't have to add to this every time
const thirdPartyCertTypes = ['FORD_BLUE_CERT', 'FORD_EV_CERT', 'FORD_CERT', 'NISSAN_SELECT_CERT', 'INFINITI_SELECT_CERT', 'CARBRAVO_OFF_CERT', 'CARBRAVO_OFF_BUDGET_CERT', 'HONDA_OEM_USED', 'ACURA_OEM_USED', 'STELLANTIS_OFF_CERT'];

const AFFIX_ID = 'affixedBottomBoundary';

export function VehicleDetailsContainer({
    isEsntialEligible = false,
    isEsntialEligibilityLoading = false,
}) {
    const {
        accelerate: [, {
            credit_app: enableSDPCreditApp,
        }],
        ads: [isAdsEnabled, { mma: isMmaEnabled, mrec: isMrecEnabled }],
        advanced_keyword_search: [enableAdvancedKeywordSearch],
        battery_health: [enableBatteryHealth, {
            images: batteryHealthImages,
            health_rating_threshold: healthRatingThreshold,
            ford_battery_health: fordBatteryHealth,
        }],
        brand: [, {
            consumer_brand: consumerBrand,
            leads_cc_flag: copySelf,
            leads_partner_name: partnerName,
        }],
        chat: [isChatEnabled, { providers: chatProviders }],
        consumer_ratings: [isConsumerRatingsEnabled, { vdp_ratings: isConsumerRatingsVdpEnabled }],
        esntial_confident_purchase: [isEsntialConfidentPurchase],
        my_wallet: [, { enable_deals: enableDeals }],
        nds_calc: [isNDSCalcEnabled],
        nds_right_rail: [enableNDSRightRail],
        one_click_lead_vdp: [enableOneClickVDP],
        remove_carfax_tile: [removeCarfaxTile],
        shopping_assistant: [enableShoppingAssistant, { vdp_assistant: enableVdpShoppingAssistant }],
        similar_inventory: [isSimilarInventoryEnabled],
        standalone_credit_app: [isStandaloneCreditAppEnabled],
        treehouse_ad: [isTreehouseAdEnabled],
        vdp_carousel_higher_placement: [isVdpCarouselHigherPlacementEnabled],
        vdp_enable_vhr_overview: [enableVhrOverview, { placement: vhrOverviewPlacement }],
        vdp_new_price_section: [enableVdpNewPriceSection],
        vehicle_highlight_specs: [enableVehicleHighlightSpecs],
    } = useFeatures([
        'accelerate',
        'ads',
        'advanced_keyword_search',
        'battery_health',
        'brand',
        'chat',
        'consumer_ratings',
        'esntial_confident_purchase',
        'my_wallet',
        'nds_calc',
        'nds_right_rail',
        'one_click_lead_vdp',
        'remove_carfax_tile',
        'shopping_assistant',
        'similar_inventory',
        'standalone_credit_app',
        'treehouse_ad',
        'vdp_carousel_higher_placement',
        'vdp_enable_vhr_overview',
        'vdp_new_price_section',
        'vehicle_highlight_specs',
    ]);

    const device = useDevice();
    const dispatch = useDispatch();
    const isXs = _get(device, 'is.xs', false);
    const isGreaterThanSm = _get(device, 'greaterThan.sm', false);
    const isLg = _get(device, 'is.lg', false);

    const { brand, brands, isBrand } = useBrand();
    const isAtcBranded = isBrand(brands.ATC_BRAND);
    const isKbbBranded = isBrand(brands.KBB_BRAND);

    const [cookies = {}] = useCookies(['ATC_ID', 'pxa_ipv4']);

    const { publicRuntimeConfig } = getConfig();
    const { atcHost, environment } = publicRuntimeConfig;

    // Get data from Redux
    const creditAppHost = useSelector(globalConfigsDuck.selectors.getCreditAppHost);
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const hasVehicleReviews = useSelector(vdpResultsDuck.selectors.hasVehicleReviews);
    const birf = useSelector(birfDuck.selectors.getPageBirf);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const memoizedQuery = useMemo(() => query || {}, [query]);
    const isEcommHyundai = memoizedQuery?.experience === 'hyundai-buy-online';
    const showEmailModal = useSelector(vdpActiveInteractionDuck.selectors.getShowEmailModal);
    const showEmailConfirmationModal = useSelector(vdpActiveInteractionDuck.selectors.getShowEmailConfirmationModal);
    const showAdditionalInventoryModal = useSelector(vdpActiveInteractionDuck.selectors.getShowAdditionalInventoryModal);
    const showModelInfoModal = useSelector(vdpActiveInteractionDuck.selectors.getShowModelInfoModal);
    const emailCTAContext = useSelector(vdpActiveInteractionDuck.selectors.getEmailCTAContext);
    const { availableOffers } = useSelector(vdpAvailableOffersDuck.selectors.getDuckState);
    const deal = useSelector(vdpResultsDuck.selectors.getDealInfo, _isEqual);

    const listingIncentives = useSelector(paymentsDuck.selectors.getListingIncentives) || {};
    const pencilingPayments = useSelector(paymentsDuck.selectors.getPencilingPayments) || {};
    const listingPayments = useSelector(paymentsDuck.selectors.getListingPayments) || {};
    const { incentiveIds } = useSelector((state) => paymentsDuck.selectors.getLowestPencilingPaymentByListingId(state, listing?.id));
    const existingDeal = useSelector((state) => dealsDuck.selectors.getDealByListingVin(state, listing?.vin));
    const memoizedExistingDeal = useMemo(() => existingDeal || {}, [existingDeal]);
    const payments = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const interestRates = useSelector((state) => state?.interestRates) || fallbackInterestRates;
    const leadProfile = useSelector(userDuck.selectors.getLeadProfile);
    const showKeyWordSearchModal = useSelector(activeInteractionDuck.selectors.getShowKeywordSearchModal);

    const { dealType, interactions = {} } = payments;
    const { paymentEmphasis, hasUpdatedWallet, hasUpdatedNDSCalc } = interactions;
    const isOneClickVDPEnabled = enableOneClickVDP && leadProfile?.isOneClickOptedIn;
    const isEsntialConfidentPurchaseEnabled = isEsntialConfidentPurchase && isEsntialEligible && listing?.esntial && !listing?.esntial?.deemphasized;
    const isElectricVehicle = listing?.fuelType?.code === 'E' || listing?.engine?.code === 'EL';
    const { cashDealPrice, disableTrade } = useWalletCalcConfig(payments, listing);
    const dealXgIdRef = useRef();
    dealXgIdRef.current = memoizedExistingDeal?.id;

    const enableVhrSection = enableVhrOverview && vhrOverviewPlacement === 'pom_and_vhr';

    // Used for handling email input field interaction analytics tagging
    const emailInputClickedRef = useRef(null);

    /**
     * Initialize the Wallet Configuration Manager
     */
    const [config, setConfig] = useState({});
    const [isConfigBuilt, setIsConfigBuilt] = useState(false);

    const { disablePriceBreakdown, paymentIncentivesFetcher, walletZip, shopperZip, dealerZip } = config;

    const zip = useSelector(userDuck.selectors.getZip) ?? memoizedQuery?.zip ?? walletZip ?? shopperZip ?? dealerZip ?? listing?.owner?.location?.address?.zip;

    // destructure listing and declare variables
    const {
        dealerConfig: { hideAds } = {},
        certifiedProduct = {},
        electricComponentInfo = {},
        id: listingId,
        hasSpecialOffer,
        listingType,
        owner = {},
        paymentServices = {},
        pricingDetail = {},
        safetyRecall,
        type,
        vhrPreview = [],
        vin,
    } = listing;

    const { incentivesUrl, digitalRetailingType, dealerSettings = {} } = paymentServices;

    const { dealerPreferredCTA, privateSeller: isPrivateSeller, sacaMmdId, id: ownerId } = owner;
    const isSingleColumn = enableVdpNewPriceSection && !isPrivateSeller;

    const updateConfiguration = useCallback((data) => {
        // override config with FF info
        data.isSingleColumn = (enableNDSRightRail && !isXs) || isSingleColumn;
        setConfig(data);
    }, [enableNDSRightRail, isXs, isSingleColumn]);

    const previousListingId = useRef();
    // Send config request with data
    useEffect(() => {
        if ((payments?.creditType && !isConfigBuilt) || (payments?.creditType && previousListingId.current !== listingId)) {
            eventBus.emit(UDS_CONFIG_REQUEST, {
                buildConfig: true,
                listing,
                payments,
                interestRates,
                isEcommHyundai,
                isSingleColumn,
            });
            setIsConfigBuilt(true);
            previousListingId.current = listingId;
        }
        // Listen for completed response to update local state
        eventBus.on(UDS_CONFIG_COMPLETED, updateConfiguration);
        eventBus.on(UDS_CONFIG_UPDATE_COMPLETED, updateConfiguration);

        return () => {
            eventBus.off(UDS_CONFIG_COMPLETED, updateConfiguration);
            eventBus.off(UDS_CONFIG_UPDATE_COMPLETED, updateConfiguration);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payments, listingId, isEcommHyundai]);

    useUDSUpdate({
        paymentsInfo: payments,
        config,
        esntialDeepLink: listing?.ecommWebsite,
    });

    const {
        isWestlakeEnabled = false,
    } = usePreQualEligibility(listing);

    const showRatingsAndReviews = isConsumerRatingsEnabled && isConsumerRatingsVdpEnabled && hasVehicleReviews;

    const getAdditionalInventory = useLeadAdditionalInventory(true, ownerId);

    const { batteryDegradationInfo: { healthRating } = {} } = electricComponentInfo;

    // exclude battery health on FBA if health rating is below 80
    const showBatteryHealth = enableBatteryHealth && electricComponentInfo?.batteryDegradationInfo && ((fordBatteryHealth && healthRating >= healthRatingThreshold) || !fordBatteryHealth);

    // Ads
    let showMrec = false;
    let showMrecMobile = false;
    let showMma = false;
    if (isAdsEnabled && (!hideAds || isPrivateSeller)) {
        showMrecMobile = isMrecEnabled && isXs;
        showMrec = isMrecEnabled && (isAtcBranded && isGreaterThanSm) || (isKbbBranded && isLg);
        showMma = isMmaEnabled && (isAtcBranded && !isXs || isKbbBranded && isLg);
    }

    // pricing & payments
    const { salePrice = '', msrp = '' } = pricingDetail;
    const hasPrice = !!(salePrice || msrp);
    const price = salePrice || msrp;

    // Wallet
    const enableStandaloneCreditApp = isStandaloneCreditAppEnabled && !!sacaMmdId && !!creditAppHost;

    useEffect(() => {
        if (!isPrivateSeller && window.location.hash === '#showEmailModal') {
            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                showEmailModal: true,
            }));
        }

        // Reset the ref when visiting another VDP
        emailInputClickedRef.current = false;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    useEffect(() => {
        if (isPrivateSeller && zip && vin) {
            PsxPrequalEligibilityCheckFetcher({ zip })
                .then(({ userLocationIsFinanceEligible }) => {
                    if (userLocationIsFinanceEligible) {
                        dispatch(inventoryDuck.creators.updateInventoryItem(listingId, {
                            creditApp: {
                                url: `/marketplace/financing?vin=${vin}`,
                                partner: 'psx',
                                ctaLabel: 'Get Pre-Qualified',
                            },
                        }));
                    }
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zip, vin]);

    useEffect(() => {
        if (getCompareListingsIds().length > 0) {
            dispatch(compareListingsDuck.creators.loadListingsData(getCompareListingsIds(), 5));
        }
        dispatch(userPreferencesDuck.creators.setCompareToggle(getCompareListingsToggle()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const existingIncentives = Object.keys(listingIncentives).length > 0 && listingIncentives?.[listingId]?.[dealType];
    const getIncentiveOffers = useCallback(async () => {
        if (existingIncentives && !existingIncentives[0]?.hasError) {
            // incentives already fetched
            return;
        }

        const incentivesParams = { dealType, zip };
        const incentives = await getIncentives({
            incentivesFetcher: paymentIncentivesFetcher,
            incentivesUrl,
            incentivesParams,
        });

        dispatch(paymentsDuck.creators.updateListingIncentives({
            [listingId]: {
                [dealType]: incentives[0]?.hasError ? incentives : transformIncentives(incentives, incentiveIds),
            },
        }));
    }, [existingIncentives, dealType, zip, paymentIncentivesFetcher, incentivesUrl, dispatch, listingId, incentiveIds]);

    const hasIncentivesOffers = !!incentivesUrl && (pencilingPayments[listingId]?.expectAvailableOffers || listingPayments[listingId]?.expectAvailableOffers || incentiveIds?.length);
    const isAccelerate = digitalRetailingType === 'accelerate';
    const enabledAmdIncentivesOffers = !hasSpecialOffer && hasIncentivesOffers && isAccelerate;

    useEffect(() => {
        if (enableVdpNewPriceSection && enabledAmdIncentivesOffers && !existingIncentives) {
            getIncentiveOffers();
        }
    }, [enabledAmdIncentivesOffers, existingIncentives, getIncentiveOffers, enableVdpNewPriceSection, listingId]);

    const {
        creditType,
        defaultLeaseDownPayment,
        downPayment,
        downPaymentPercent,
    } = getAppliedDealerSettings({
        dealerSettings,
        walletInteractions: interactions,
        price,
        payments,
        nationalRates: interestRates,
    });

    const hasExistingDeal = enableDeals && !_isEmpty(memoizedExistingDeal);

    const handleOpenSDP = useCallback(() => dispatch(vdpActiveInteractionDuck.creators.setKeys({
        emailCTAContext: '',
        showEmailModal: false,
    })), [dispatch]);

    const handleErrorOpenSDP = useCallback(() => dispatch(vdpActiveInteractionDuck.creators.setKeys({
        emailCTAContext: 'calculatorOfferSummary',
        showEmailModal: true,
    })), [dispatch]);

    const handleEditSettingsClick = () => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showEmailModal: true,
            showEditEmailModal: true,
        }));
    };

    const paymentsInfo = useMemo(() => ({
        ...payments,
        creditType,
        downPayment: downPayment || calculateDownPayment(price, { downPayment, downPaymentPercent }),
        dueAtSigning: defaultLeaseDownPayment || calculateDownPayment(price, {
            downPayment: paymentEmphasis ? downPayment : defaultLeaseDownPayment,
            downPaymentPercent: paymentEmphasis ? downPaymentPercent : 0.10,
        }),
    }), [creditType, defaultLeaseDownPayment, downPayment, downPaymentPercent, paymentEmphasis, payments, price]);

    const renderDigitalRetailOverlayCTA = (
        <DigitalRetailOverlayCTA
            listedPrice={price}
            listing={listing}
            paymentsInfo={paymentsInfo}
            paymentsDuck={paymentsDuck}
            onClick={handleOpenSDP}
            onError={handleErrorOpenSDP}
            uiContext="incentives"
            userDuck={userDuck}
            isLoggedIn={isLoggedIn}
        />
    );

    const prequalCtaProps = useMemo(() => ({
        enableSDPCreditApp: enableSDPCreditApp && isAccelerate,
        enableSACAStickyHeader: enableStandaloneCreditApp && !isAccelerate,
        onError: handleErrorOpenSDP,
        listedPrice: price,
        userDuck,
        isLoggedIn,
        paymentsInfo,
    }), [enableSDPCreditApp, enableStandaloneCreditApp, handleErrorOpenSDP, isAccelerate, isLoggedIn, paymentsInfo, price]);

    const onEmailCTAClick = useCallback((context) => {
        if (context === 'incentives' && hasSpecialOffer) {
            dispatch(remoteActions.getAvailableOffers({ id: listingId }));
        }
        if (context === 'incentives' && enabledAmdIncentivesOffers) {
            getIncentiveOffers();
        }
        if (context === 'contactTheDealer') {
            sendClick(ownerEmailClick, null, {
                par: 'v_lstg',
                includePageId: true,
                inventoryId: listingId,
                ownerId,
                context: 'contactTheDealer',
            });
        }
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: context,
            showEmailModal: true,
        }));
        dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listingId]));
    }, [hasSpecialOffer, enabledAmdIncentivesOffers, dispatch, listingId, getIncentiveOffers, ownerId]);

    // Ref variable to store the vi360 zIndex before its modified
    const vi360ZIndex = useRef(null);

    // This use effect is needed to reposition the email modal zIndex in case the vi360 modal exists on the page.
    useEffect(() => {
        const vi360Modal = document.getElementsByClassName('ff_modal')?.[0];
        if (vi360Modal) {

            if (!vi360ZIndex.current) {
                const vi360ModalComputedZIndex = window.getComputedStyle(vi360Modal)?.zIndex;
                vi360ZIndex.current = vi360ModalComputedZIndex;
            }

            const modalBackdrop = document.getElementsByClassName('ff_modal_open')?.[0];

            if (modalBackdrop) {
                const modalBackdropzIndex = window.getComputedStyle(modalBackdrop, '::after')?.zIndex;
                if (showEmailConfirmationModal || showEmailModal) {
                    vi360Modal.style.zIndex = parseInt(modalBackdropzIndex, 10) - 1;
                }

                if (!showEmailConfirmationModal && !showEmailModal && vi360ZIndex.current) {
                    vi360Modal.style.zIndex = vi360ZIndex.current;
                }
            }
        }
    }, [showEmailModal, showEmailConfirmationModal]);

    // for Hyundai experience
    const disableContactCTAs = isEcommHyundai && isEsntialEligible;

    const showNDSRightRail = enableNDSRightRail && isGreaterThanSm;
    // enable Westlake Prequal CTA in mobile state
    const enableMobileWestlakeCTA = isWestlakeEnabled && isXs;

    // certifiedProduct
    const { id: certifiedType } = certifiedProduct;

    // chat
    const isChatEventEnabled = isChatEnabled && chatProviders.includes(owner?.chat?.provider ?? '') && !disableContactCTAs;

    // Incentives
    const isIncentivesOfferEnabled = listingType?.toLowerCase() === 'new' && (hasSpecialOffer || enabledAmdIncentivesOffers);

    const pixallFirstPartyId = useMemo(() => getFirstPartyVistorId(), []);
    const pixallThirdPartyId = useMemo(() => getPixallId() || 'nl', []);

    const fireTelmetricsTag = useCallback((phoneNumber) => {
        const data = {
            birf,
            listing: {
                ...listing,
                vdpURL: getAbsolutePathWithListingId(window?.location?.origin, listingId),
            },
            phoneNumber,
        };
        sendClick(telMetricsPhoneTextClick, {}, data);
    }, [birf, listing, listingId]);

    const persistDealData = useCallback(() => {
        if (isNDSCalcEnabled && enableDeals && Object.keys(deal).length) {
            PersistDealDataService(atcHost, {
                listingId,
                pixallId: pixallFirstPartyId || pixallThirdPartyId,
                dealData: deal,
            });
        }
    }, [isNDSCalcEnabled, enableDeals, deal, atcHost, listingId, pixallFirstPartyId, pixallThirdPartyId]);

    const phoneAndTextHandler = useCallback((phoneNumber) => {
        fireTelmetricsTag(phoneNumber);
        persistDealData();
    }, [fireTelmetricsTag, persistDealData]);

    const setEmailConfirmationModal = useCallback((state) => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showEmailConfirmationModal: state,
        }));
    }, [dispatch]);

    const handleEmailInputInteraction = useCallback((event, context = '') => {
        // Should fire once per VDP when user interact with email input form for the first time
        if (!emailInputClickedRef.current) {
            sendClick(emailFormEngagementClick, event, { inventoryId: listing.id, uiContext: context });
            emailInputClickedRef.current = true;
        }
    }, [listing.id]);

    const updateMyWallet = useCallback((data) => {
        dispatch(paymentsDuck.creators.updateMyWallet(data));
    }, [dispatch]);

    const [oneClickEmailSent, setOneClickEmailSent] = useState(false);

    const [oneClickAdditionalInventory, setOneClickAdditionalInventory] = useState(false);

    useEffect(() => {
        setOneClickEmailSent(false);
    }, [listing?.id]);

    const [dealToSubmit, setDealToSubmit] = useState({});
    useEffect(() => {
        const { deal: dealData } = getDeal(listing, payments, interestRates, false, cashDealPrice, disableTrade);
        setDealToSubmit(dealData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(payments), // Use deep object comparison not referential equality
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(pricingDetail), // Use deep object comparison not referential equality
    ]);

    // This closes all existing modals when sending an email using additionalInventory in case additionalInventory is opened through another modal
    const handleAdditionalInventoryModalClose = () => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: '',
            showEmailModal: false,
            showEditEmailModal: false,
            showEmailConfirmationModal: false,
        }));

        if (!oneClickAdditionalInventory) {
            setOneClickAdditionalInventory(false);
        }
    };

    // Sets the emailCTAContext to be used when opening and closing the additonal inventory modal
    const handleAdditionalInventoryModalOpen = () => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: 'multi',
        }));
    };

    const handleSendOneClickEmail = useCallback(async (event) => {

        const isUserInvolved = paymentEmphasis || hasUpdatedWallet || hasUpdatedNDSCalc;
        const enableSaveDeal = hasPrice && enableDeals && isNDSCalcEnabled && isUserInvolved;

        sendClick(ownerEmailSent, event, {
            inventoryId: listingId,
            hasListingType: true,
            includeWalletData: false,
            par: 'v_lstg',
            context: 'contactTheDealer',
        });

        const { emailResult, responseDealXgId } = await sendEmailAndSaveDeal({
            cookies,
            deal: dealToSubmit,
            dealXgIdRef,
            dealsDuck,
            disableTrade,
            dispatch,
            emailOwnerFetcher: EmailOwnerFetcher,
            enableSaveDeal,
            env: publicRuntimeConfig.environment === 'prod' ? 'prod' : 'staging',
            formData: {
                comments: 'Please confirm vehicle availability.',
                firstName: leadProfile.firstName,
                fromEmailAddress: leadProfile?.email,
                lastName: leadProfile?.lastName,
                phoneNumber: leadProfile?.phone,
            },
            isAccelerate,
            listing,
            oneClick: true,
            originPage: 'vdp',
            owner,
            paymentsDuck,
            pixallFirstPartyId,
            pixallThirdPartyId,
            saveDealXgFetcher,
            userZip: zip,
            walletId: payments?.walletId,
        });

        dealXgIdRef.current = responseDealXgId;

        if (emailResult?.email?.status === 'SUCCESS') {
            setOneClickAdditionalInventory(true);
            setOneClickEmailSent(true);
            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                showEmailConfirmationModal: true,
            }));
        }

    }, [cookies, dispatch, leadProfile, listing, listingId, pixallFirstPartyId, pixallThirdPartyId, zip, dealToSubmit, enableDeals, hasPrice, hasUpdatedNDSCalc, hasUpdatedWallet, isAccelerate, isNDSCalcEnabled, paymentEmphasis, disableTrade, owner, payments?.walletId, publicRuntimeConfig.environment]);

    const handleVHROpenNewTab = useCallback(async () => {
        const vhrTiles = listing?.vhrTiles || [];

        if (vhrTiles.length === 0) {
            return;
        }

        const { link, tileType } = configureExperianTile(brand, vhrTiles[0]);
        const vhrLink = link?.href;

        if (!vhrLink) {
            return;
        }

        const isCarfaxTile = tileType?.split('_')[0] === 'CARFAX';

        try {
            await new Promise((resolve) => { setTimeout(resolve, 1000); });

            if (isCarfaxTile && removeCarfaxTile) {
                window.open(atob(vhrLink), '_blank');
            } else {
                window.open(vhrLink, '_blank');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to open the link:', error);
        }
    }, [brand, listing?.vhrTiles, removeCarfaxTile]);

    const handleContactDealerCTA = useCallback((data) => {
        const { placement } = data;

        switch (placement) {
            case 'vi360':
                onEmailCTAClick('vi360');
                break;
            case 'lenderPaymentCTA':
                if (isOneClickVDPEnabled) {
                    handleSendOneClickEmail();
                } else {
                    onEmailCTAClick('contactTheDealer');
                }
                break;
            default:
                break;
        }
    }, [handleSendOneClickEmail, isOneClickVDPEnabled, onEmailCTAClick]);

    useEffect(() => {
        eventBus.on(WALLET_EMAIL_DEALER_REQUEST, handleContactDealerCTA);

        return () => {
            eventBus.off(WALLET_EMAIL_DEALER_REQUEST, handleContactDealerCTA);
        };
    }, [payments, interestRates, isOneClickVDPEnabled, listing, updateConfiguration, onEmailCTAClick, handleSendOneClickEmail, handleContactDealerCTA]);

    const [isBreakdownConfirmation, setIsBreakdownConfirmation] = useState(false);

    useEffect(() => {
        if (showEmailModal === false) {
            setIsBreakdownConfirmation(false);
        }
    }, [showEmailModal]);

    const enableShowOfferSingleClick = !isAccelerate && !!leadProfile?.email && !!leadProfile?.firstName && !!leadProfile?.lastName && !hasExistingDeal;

    const onSendOfferClick = useCallback(async (context) => {
        if (context === 'incentives' && hasSpecialOffer) {
            dispatch(remoteActions.getAvailableOffers({ id: listingId }));
        }
        if (context === 'incentives' && enabledAmdIncentivesOffers) {
            getIncentiveOffers();
        }

        const isUserInvolved = paymentEmphasis || hasUpdatedWallet || hasUpdatedNDSCalc;
        const enableSaveDeal = hasPrice && enableDeals && isNDSCalcEnabled && isUserInvolved;
        const saveIcoData = listing?.esntial && !listing?.esntial?.deemphasized && listing?.ecommWebsite && !!Object.keys(listing?.ecommWebsite).length && payments?.tradeIn?.ico?.length;

        const vehicleTitle = listing?.title || listing?.listingtitle || 'vehicle';
        const priceClause = price ? ` for ${formatCurrency(price)}` : '';
        const emailMessage = `I want to make an offer on your ${vehicleTitle} listed${priceClause}.`;

        const { emailData, emailResult, responseDealXgId } = await sendEmailAndSaveDeal({
            consumerBrand,
            icoTradeIn: (saveIcoData ? payments?.tradeIn?.ico?.[0] : undefined),
            cookies,
            copySelf,
            deal: dealToSubmit,
            dealXgIdRef,
            dealsDuck,
            disableTrade,
            dispatch,
            emailOwnerFetcher: EmailOwnerFetcher,
            enableSaveDeal,
            env: publicRuntimeConfig.environment === 'prod' ? 'prod' : 'staging',
            formData: {
                comments: emailMessage,
                firstName: leadProfile.firstName,
                fromEmailAddress: leadProfile?.email,
                lastName: leadProfile?.lastName,
                phoneNumber: leadProfile?.phone,
            },
            includeWalletData: true,
            isAccelerate,
            listing,
            originPage: 'vdp',
            owner,
            partnerName,
            paymentsDuck,
            pixallFirstPartyId,
            pixallThirdPartyId,
            saveDealXgFetcher,
            userZip: zip,
            walletId: payments?.walletId,
        });

        dealXgIdRef.current = responseDealXgId;

        if (emailResult?.email?.status === 'SUCCESS') {
            setIsBreakdownConfirmation(true);

            // Fires the email tag
            sendClick(ownerEmailSent, null, {
                ...emailData,
                deal: dealToSubmit,
                inventoryId: listingId,
                par: 'v_lstg',
                isChipEnabled: true,
                serviceInquiry: 'confirmAvailability',
                inquiryLabel: "This Vehicle's Availability",
                context: 'calculatorDealSubmitted',
                selectedVrsData: listing?.selectedVrsData,
                trimSelected: listing?.trimSelected,
            });

            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                emailCTAContext: context,
                showEmailModal: true,
            }));

            dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listingId]));
        }

    }, [consumerBrand, payments?.tradeIn?.ico, dispatch, hasSpecialOffer, enabledAmdIncentivesOffers, listingId, getIncentiveOffers, cookies, leadProfile, listing, pixallFirstPartyId, pixallThirdPartyId, zip, dealToSubmit, enableDeals, hasPrice, hasUpdatedNDSCalc, hasUpdatedWallet, isAccelerate, isNDSCalcEnabled, paymentEmphasis, disableTrade, owner, payments?.walletId, publicRuntimeConfig.environment, price, copySelf, partnerName]);

    const handleEmailCTAClick = useCallback(() => {
        if (isPrivateSeller || !enableShowOfferSingleClick) {
            return onEmailCTAClick;
        }
        return onSendOfferClick;
    }, [isPrivateSeller, enableShowOfferSingleClick, onEmailCTAClick, onSendOfferClick]);

    const emailOwnerProps = useMemo(() => ({
        activeEmailListingDuck: vdpActiveEmailListingDuck,
        activeInteractionDuck,
        alternateFormContent: (
            <DigitalRetailOverlayCTA
                listing={listing}
                paymentsDuck={paymentsDuck}
                uiContext="testDrive"
                userDuck={userDuck}
                isLoggedIn={isLoggedIn}
                dealsDuck={dealsDuck}
            />
        ),
        enableConsumerMetadata: true,
        currentPageNameDuck,
        saveDealXgFetcher,
        env: environment === 'prod' ? 'prod' : 'staging',
        handleAdditionalInventoryModalClose,
        handleAdditionalInventoryModalOpen,
        handleEditSettingsClick,
        handleEmailInputInteraction,
        setOneClickEmailSent,
        fetcher: EmailOwnerFetcher,
        interestRateDuck,
        isLoggedIn,
        nextStepsProps: {
            getAdditionalInventory,
            SimilarListingsFetcher,
            nextSteps: <EmailFormNextStepsContainer />,
        },
        emailCTAContext,
        ownerId,
        oneClickAdditionalInventory,
        oneClickEmailSent,
        oneClickOptIn: enableOneClickVDP,
        paymentsDuck,
        dealsDuck,
        setEmailConfirmationModal,
        updateMyWallet,
        smsSubscribeFetcher: SmsSubscribeFetcher,
        showEmailConfirmationModal,
        userDuck,
        isBreakdownConfirmation,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [updateMyWallet, isLoggedIn, ownerId, showEmailConfirmationModal, showEmailModal, getAdditionalInventory, listing]);

    const emailCTAProps = useMemo(() => ({
        buttonStyle: dealerPreferredCTA === 'WEBSITE' ? 'secondary' : 'primary',
        listingId,
        onClick: onEmailCTAClick,
    }), [onEmailCTAClick, listingId, dealerPreferredCTA]);

    const oneClickCTAProps = useMemo(() => ({
        handleSendOneClickEmail,
        oneClickEmailSent,
    }), [handleSendOneClickEmail, oneClickEmailSent]);

    // setting LazyLoad preserve heights
    let ndsHeight;
    let psxComponentsHeight;

    switch (device?.mediaType) {
        case 'lg':
            ndsHeight = showNDSRightRail ? 1013 : 895;
            break;
        case 'sm':
            ndsHeight = 919;
            psxComponentsHeight = 515;
            break;
        case 'xs':
            ndsHeight = 1267;
            psxComponentsHeight = 480;
            break;
        case 'md':
        default:
            ndsHeight = showNDSRightRail ? 1082 : 954;
            psxComponentsHeight = 430;
    }

    const renderPrimaryVdpCtaContainer = useMemo(() => (
        <PrimaryVdpCallsToActionContainer
            key="calls-to-action"
            emailCTAProps={emailCTAProps}
            disableContactCTAs={disableContactCTAs}
            oneClickCTAProps={oneClickCTAProps}
            phoneAndTextHandler={phoneAndTextHandler}
        />
    ), [emailCTAProps, disableContactCTAs, oneClickCTAProps, phoneAndTextHandler]);

    /**
     * STICKY HEADER
     */
    const sectionStickyHeader = useMemo(() => {
        if (isPrivateSeller) {
            return (
                <>
                    <LazyComponent>
                        <StickyHeaderContainer />
                    </LazyComponent>
                    <LazyComponent>
                        <PsxBubbleChat />
                    </LazyComponent>
                </>
            );
        }
        return (
            <LazyComponent>
                <StickyHeaderContainer
                    emailCTAProps={emailCTAProps}
                    existingDeal={memoizedExistingDeal}
                    disableContactCTAs={disableContactCTAs}
                    oneClickCTAProps={oneClickCTAProps}
                    phoneClickHandler={phoneAndTextHandler}
                    prequalCtaProps={prequalCtaProps}
                />
            </LazyComponent>
        );
    }, [emailCTAProps, disableContactCTAs, isPrivateSeller, memoizedExistingDeal, oneClickCTAProps, phoneAndTextHandler, prequalCtaProps]);

    /**
     * PSX VERIFICATION
     */
    const sectionPsxVerification = useMemo(() => (isPrivateSeller && (
        <div
            style={{ minHeight: '45px' }}
            key="verification"
        >
            <LazyComponent key="verification">
                <PsxVerificationContainer key="verification" />
            </LazyComponent>
        </div>
    )), [isPrivateSeller]);

    // If the VHR preview list includes things we don't have VHR cells for in the grid, they will be filtered out
    const vhrOverviewList = useMemo(() => getVhrOverviewList(vhrPreview, safetyRecall), [vhrPreview, safetyRecall]);

    const vhrOverviewContent = useMemo(() => (vhrOverviewList.length > 0) && (
        <LazyComponent
            key="vhrOverview"
            fallback={<LoadingSpinner id="vhrOverviewContainer" />}
        >
            <VhrOverviewContainer onEmailCtaClick={handleEmailCTAClick()} />
        </LazyComponent>
    ), [vhrOverviewList, handleEmailCTAClick]);

    /**
     * BANNER
     */
    const sectionBanner = useMemo(() => {
        if (isPrivateSeller) {
            return [
                <ListingErrorContainer key="psx_error" />,
                <VehicleDetailsTitleContainer
                    key="psx_title"
                    showRatingsAndReviews={showRatingsAndReviews}
                    onEmailCTAClick={onEmailCTAClick}
                />,
            ];
        }
        return [
            <ListingErrorContainer key="error" />,
            <VehicleDetailsTitleContainer
                key="title"
                onEmailCTAClick={onEmailCTAClick}
            />,
        ];
    }, [isPrivateSeller, showRatingsAndReviews, onEmailCTAClick]);

    /**
     * SIMILAR INVENTORY
     */
    const sectionSimilarInventory = (isSimilarInventoryEnabled && isVdpCarouselHigherPlacementEnabled) && (
        <LazyComponent
            key="owner_similar_inventory"
            fallback={similarIventoryFallback}
        >
            <SimilarInventoryContainer />
        </LazyComponent>
    );

    /**
     * MEDIA
     */
    const sectionMedia = useMemo(() => [
        <MediaGallery
            key="media"
            emailCTAProps={emailCTAProps}
            disableContactCTAs={disableContactCTAs}
            phoneClickHandler={phoneAndTextHandler}
        />,
    ], [emailCTAProps, disableContactCTAs, phoneAndTextHandler]);

    /**
     * SIDEBAR
     */
    const sectionSidebar = useMemo(() => {
        if (isPrivateSeller) {
            return [
                ((
                    <PricingSnapshotContainer
                        key="pricing_prequal"
                        config={config}
                    />
                )),

                isXs && (
                    <React.Fragment key="psx_mobile_frag">
                        <ClientOnly
                            fallback={(
                                <LoadingSpinner
                                    className="margin-top-4 margin-bottom-5"
                                    height={40}
                                />
                            )}
                            key="psx_button"
                        >
                            <PsxButton
                                block
                                className="margin-top-4 margin-bottom-5"
                                uiContext="makeOffer"
                                loadingComponent={( // Handles the delay before psx.action is ready by showing a loading component to keep layout stable
                                    <LoadingSpinner
                                        className="margin-top-4 margin-bottom-5"
                                        height={40}
                                    />
                                )}
                            >
                                Make Offer
                            </PsxButton>

                        </ClientOnly>
                        {!enableVehicleHighlightSpecs && <SpecificationsGridContainer key="psx_specs_mobile" />}
                        <VehicleFeaturesContainer />
                    </React.Fragment>
                ),
                showNDSRightRail && (
                    <LazyComponent
                        key="nds_right_rail"
                        fallback={(
                            <LoadingSpinner
                                // required for jumplink
                                id="nativeDealContainer"
                                height={ndsHeight}
                            />
                        )}
                    >
                        <Panel>
                            <MyWalletContainer
                                onEmailCTAClick={onEmailCTAClick}
                                enableCreditAppLink={enableStandaloneCreditApp}
                                isRightRail
                                config={config}
                            />
                        </Panel>
                    </LazyComponent>
                ),
                <AffixedSidebar
                    id={AFFIX_ID}
                    affixedGreaterThan="sm"
                    key="psx_sidebar"
                >
                    <ClientOnly
                        key="psx_sidebar"
                        fallback={<LoadingSpinner height={psxComponentsHeight} />}
                    >
                        <PsxPrimerContainer />
                        <PsxLeadsContainer />
                        {showMrec && <ShowcaseAdSlotContainer />}
                    </ClientOnly>
                </AffixedSidebar>,
            ];
        }

        let sharedComponents = [
            isEsntialConfidentPurchaseEnabled && !isEsntialEligibilityLoading && <PurchaseWithConfidenceSnapshotContainer key="pwc-snapshot" />,
            isXs && isEsntialConfidentPurchaseEnabled && renderPrimaryVdpCtaContainer,
            showNDSRightRail && (
                <LazyComponent
                    key="nds_right_rail"
                    fallback={(
                        <LoadingSpinner
                            // required for jumplink
                            id="nativeDealContainer"
                            height={ndsHeight}
                        />
                    )}
                >
                    <Panel>
                        <MyWalletContainer
                            onEmailCTAClick={onEmailCTAClick}
                            enableCreditAppLink={enableStandaloneCreditApp}
                            isRightRail
                            config={config}
                        />
                    </Panel>
                </LazyComponent>
            ),
        ];
        sharedComponents = [
            <PricingSnapshotContainer
                key="pricing_prequal"
                emailCTAProps={isIncentivesOfferEnabled ? emailCTAProps : undefined}
                config={config}
            />,
            !enableMobileWestlakeCTA && (
                <LazyComponent
                    key="prequal_cta_container"
                    fallback={null}
                >
                    <PreQualCTAContainer
                        uiContext="simplified"
                        emailCTAProps={emailCTAProps}

                    />
                </LazyComponent>
            ),
            isXs && !isEsntialConfidentPurchaseEnabled && renderPrimaryVdpCtaContainer,
            isXs && (
                <React.Fragment key="specs_features_mobile">
                    {!enableVehicleHighlightSpecs && <SpecificationsGridContainer key="specs_mobile" />}
                    <VehicleFeaturesContainer />
                </React.Fragment>
            ),
            ...sharedComponents,
        ];
        if (!isXs) {
            sharedComponents = [
                ...sharedComponents,
                <PrimaryVdpCallsToActionContainer
                    emailCTAProps={emailCTAProps}
                    disableContactCTAs={disableContactCTAs}
                    key="primary-vdp-cta"
                />,
            ];
        }
        return [
            ...sharedComponents,
            enableVhrSection && isXs && vhrOverviewContent,
            <AffixedSidebar
                id={AFFIX_ID}
                affixedGreaterThan="sm"
                key="sidebar"
            >
                <ClientOnly key="sidebar">
                    <DealerLeadsContainer
                        emailCTAProps={emailCTAProps}
                        emailOwnerProps={emailOwnerProps}
                        disableContactCTAs={disableContactCTAs}
                        persistDealData={persistDealData}
                        phoneAndTextHandler={phoneAndTextHandler}
                        isEsntialEligibilityLoading={isEsntialEligibilityLoading}
                    />
                </ClientOnly>
            </AffixedSidebar>,
        ];
    }, [
        config,
        emailCTAProps,
        emailOwnerProps,
        enableMobileWestlakeCTA,
        enableStandaloneCreditApp,
        enableVhrSection,
        disableContactCTAs,
        isEsntialConfidentPurchaseEnabled,
        isIncentivesOfferEnabled,
        isPrivateSeller,
        isXs,
        ndsHeight,
        onEmailCTAClick,
        persistDealData,
        phoneAndTextHandler,
        psxComponentsHeight,
        renderPrimaryVdpCtaContainer,
        showMrec,
        showNDSRightRail,
        vhrOverviewContent,
        isEsntialEligibilityLoading,
        enableVehicleHighlightSpecs,
    ]);

    /**
     * AD SLOTS
     */
    const renderAdSlots = useMemo(() => [
        (showMma) && (
            <LeaderBoardAdSlotContainer
                key="leaderboard-adslot-container"
                isInColumn
            />
        ),
        (showMrecMobile) && (
            <ShowcaseAdSlotContainer
                key="showcase-adslot-container"
                className={clsx(
                    'padding-vertical-4',
                    {
                        'margin-bottom-5': isPrivateSeller,
                        'padding-vertical-md-5': !isPrivateSeller,
                    }
                )}
            />
        ),
    ], [isPrivateSeller, showMma, showMrecMobile]);

    /**
     * UPPER DETAILS
     */
    const sectionUpperDetails = useMemo(() => {

        const renderMyWalletContainer = hasPrice && !showNDSRightRail && (
            <LazyComponent
                key={isPrivateSeller ? 'psx_nds' : 'nds'}
                fallback={(
                    <LoadingSpinner
                        // required for jumplink
                        id="nativeDealContainer"
                        height={ndsHeight}
                    />
                )}
            >
                <MyWalletContainer
                    onEmailCTAClick={handleEmailCTAClick()}
                    enableCreditAppLink={enableStandaloneCreditApp}
                    config={config}
                />
            </LazyComponent>
        );

        const renderLenderPreQualWrapper = (
            <LenderPreQualWrapper
                key="lenderPreQualWrapper"
                listing={listing}
                zip={zip}
            />
        );

        if (isPrivateSeller) {
            return [
                enableVhrSection && !isXs && vhrOverviewContent,
                renderMyWalletContainer,
                renderLenderPreQualWrapper,
                <LazyComponent
                    key="psx_vehicle_details"
                    height={additionalVehicleDetailsHeight}
                >
                    <AdditionalVehicleDetailsContainer />
                </LazyComponent>,
                showRatingsAndReviews && (
                    <LazyComponent
                        key="psx_ratings_reviews_lazy"
                        fallback={<Anchor id="rating-reviews-section" />}
                        height={ratingsAndReviewsHeight}
                    >
                        <RatingsAndReviewsContainer
                            key="ratings_reviews"
                            isInColumn={isPrivateSeller}
                        />
                    </LazyComponent>
                ),
                <LazyComponent
                    key="psx_pwc_container"
                    height={purchaseConfidenceHeight}
                    fallback={(
                        <LoadingSpinner id="purchaseConfidence" />
                    )}
                >
                    <PurchaseWithConfidenceContainer />
                </LazyComponent>,
                ...renderAdSlots,
            ];
        }

        return [
            enableVhrSection && !isXs && vhrOverviewContent,
            enableVdpNewPriceSection ? (
                <PriceSection
                    key="price_section"
                    renderMyWalletContainer={renderMyWalletContainer}
                    renderLenderPreQualWrapper={renderLenderPreQualWrapper}
                    getIncentiveOffers={getIncentiveOffers}
                    emailOwnerProps={emailOwnerProps}
                    enabledAmdIncentivesOffers={enabledAmdIncentivesOffers}
                />
            ) : [renderMyWalletContainer, renderLenderPreQualWrapper],
            <ClientOnly
                key="vehicle_details"
                fallback={<LoadingSpinner height={additionalVehicleDetailsHeight.xs} />}
            >
                <AdditionalVehicleDetailsContainer />
            </ClientOnly>,
            (isTreehouseAdEnabled && isElectricVehicle) && (
                <ClientOnly key="treehouse_ad">
                    <TreehouseAdPlacement
                        isXs={isXs}
                        orientation={isXs ? 'portrait' : 'landscape'}
                        listing={listing}
                    />
                </ClientOnly>
            ),
            (showBatteryHealth) && (
                <LazyComponent
                    key="battery_health"
                    height={batteryHealthHeight}
                    fallback={(
                        <LoadingSpinner
                            id="batteryHealthSection"
                        />
                    )}
                >
                    <BatteryHealthContainer images={batteryHealthImages} />
                </LazyComponent>
            ),
            <LazyComponent
                fallback={(
                    <LoadingSpinner
                        id="pricingSection"
                    />
                )}
                key="pricing_details"
                height={pricingHeight}
            >
                <PricingDetailsContainer
                    emailCTAProps={emailCTAProps}
                    disablePriceBreakdown={disablePriceBreakdown}
                />
            </LazyComponent>,
            (type === 'Certified' || thirdPartyCertTypes.includes(certifiedType)) && (
                <LazyComponent
                    key="certified"
                    fallback={<Anchor id="warrantySection" />}
                    height={certifiedProgramHeights}
                >
                    <CertifiedProgramOfferContainer />
                </LazyComponent>
            ),
            <LazyComponent
                key="pwc_container"
                fallback={(
                    <LoadingSpinner
                        id="purchaseConfidence"
                    />
                )}
                height={purchaseConfidenceHeight}
            >
                <PurchaseWithConfidenceContainer
                    emailCTAProps={emailCTAProps}
                    disableContactCTAs={disableContactCTAs}
                    onEmailCTAClick={onEmailCTAClick}
                />
            </LazyComponent>,
            ...renderAdSlots,
            (showRatingsAndReviews && (
                <LazyComponent
                    key="ratings_reviews_lazy"
                    fallback={<Anchor id="rating-reviews-section" />}
                    height={ratingsAndReviewsHeight}
                >
                    <RatingsAndReviewsContainer isInColumn />
                </LazyComponent>
            )),
        ];
    }, [
        batteryHealthImages,
        certifiedType,
        config,
        disablePriceBreakdown,
        emailCTAProps,
        enableStandaloneCreditApp,
        enableVhrSection,
        handleEmailCTAClick,
        hasPrice,
        disableContactCTAs,
        isElectricVehicle,
        isPrivateSeller,
        isTreehouseAdEnabled,
        isXs,
        listing,
        ndsHeight,
        onEmailCTAClick,
        renderAdSlots,
        showBatteryHealth,
        showNDSRightRail,
        showRatingsAndReviews,
        type,
        vhrOverviewContent,
        zip,
        enableVdpNewPriceSection,
        emailOwnerProps,
        enabledAmdIncentivesOffers,
        getIncentiveOffers,
    ]);

    /**
     * SUPPORT CONTAINERS
     */
    const sectionSupportContainers = useMemo(() => {
        if (isPrivateSeller) {
            return null;
        }
        return [
            <VehicleDetailsEmailModalContainer
                {...emailOwnerProps}
                {...(isIncentivesOfferEnabled && {
                    incentivesProps: {
                        additionalContent: (
                            <ModalScrollPanel>
                                <AvailableOffers
                                    offers={availableOffers || listingIncentives[listingId]?.[dealType]}
                                    wrappedInModal
                                />
                            </ModalScrollPanel>
                        ),
                        sideBarToModalTitle: 'Available Offers',
                        uiContext: 'split',
                        nextStepsProps: {
                            ...emailOwnerProps.nextStepsProps,
                        },
                        ...(enabledAmdIncentivesOffers && !hasExistingDeal && {
                            footerContent: renderDigitalRetailOverlayCTA,
                            buttonStyle: 'secondary',
                        }),
                    },
                })}
                ndsOfferBreakdownProps={{
                    additionalContent: (
                        <OfferSummary
                            config={config}
                            isBreakdownConfirmation={isBreakdownConfirmation}
                        />
                    ),
                    sideBarToModalTitle: 'Send Offer',
                }}
                vhrLeadGateProps={{
                    sideBarToModalTitle: 'Vehicle History Report',
                    uiContext: 'vehicleHistoryReport',
                    handleVHROpenNewTab,
                }}
                testDriveProps={{
                    sideBarToModalTitle: 'Schedule Test Drive',
                    uiContext: 'testDrive',
                }}
                key="email_modal"
            />,
            isChatEventEnabled && (
                <OwnerChatEvents
                    listingId={listingId}
                    ownerId={ownerId}
                    key="chat_events"
                    chatCallBack={persistDealData}
                />
            ),
            enableStandaloneCreditApp && (
                <StandaloneCreditAppContainer
                    key="saca_modal"
                />
            ),
            (enableShoppingAssistant && enableVdpShoppingAssistant) && (
                <ClientOnly key="vdp_shopping_assistant">
                    <VdpShoppingAssistantContainer />
                </ClientOnly>
            ),
            (enableAdvancedKeywordSearch && showKeyWordSearchModal) && (
                <KeywordSearchModalContainer />
            ),
            showModelInfoModal && (
                <ModelInformationModal
                    key="model_information_modal"
                />
            ),
            showAdditionalInventoryModal && (
                <AdditionalInventoryModal
                    show={showAdditionalInventoryModal}
                    dealToSubmit={dealToSubmit}
                    isXs={isXs}
                    listing={listing}
                    {...emailOwnerProps}
                />
            ),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPrivateSeller, emailOwnerProps, listing, enableStandaloneCreditApp, isChatEventEnabled, persistDealData, availableOffers, isIncentivesOfferEnabled, listingIncentives, config, dealType, listingId, enabledAmdIncentivesOffers, hasExistingDeal, showModelInfoModal, showKeyWordSearchModal]);

    /**
     * This return block should only contain the basic layout of the VDP
     * any new containers or components should be added
     * to the appropriate sections above.
     */
    return (
        <>
            {sectionStickyHeader}
            <StickyContainer>
                <VdpPaginationContainer key="pagination" />
                <div className={clsx(
                    'container',
                    {
                        'margin-top-5': !isPrivateSeller,
                        'margin-top-3': isPrivateSeller,
                    }
                )}
                >
                    <div className="row">
                        {/* Any components in these sections should NOT be
                          * wrapped in a PageSection or .container
                          */}
                        <div className="col-xs-12 col-sm-7 col-md-8">
                            {sectionPsxVerification}
                            {sectionMedia}
                            {sectionBanner}
                            {!isXs && (
                                <>
                                    {!enableVehicleHighlightSpecs && <SpecificationsGridContainer key="specs" />}
                                    <Anchor id={AFFIX_ID} />
                                    <VehicleFeaturesContainer />
                                </>
                            )}
                            {isGreaterThanSm && sectionSimilarInventory}
                            {isGreaterThanSm && sectionUpperDetails}

                        </div>
                        <div className="col-xs-12 col-sm-5 col-md-4 padding-left-md-3">
                            {sectionSidebar}
                        </div>
                        {!isGreaterThanSm && (
                            <div className="col-xs-12">
                                {sectionSimilarInventory}
                                {sectionUpperDetails}
                            </div>
                        )}
                    </div>
                </div>
                <LowerDetails
                    chatClickHandler={persistDealData}
                    disableContactCTAs={disableContactCTAs}
                    onEmailCTAClick={onEmailCTAClick}
                    phoneAndTextHandler={phoneAndTextHandler}
                />
            </StickyContainer>
            {sectionSupportContainers}
        </>
    );
}

export default memo(VehicleDetailsContainer, _isEqual);
